import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ArticleIcon from "@mui/icons-material/Article";
import SecurityIcon from "@mui/icons-material/Security";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import config from "../../config";

const OTHER_REASON = "other";

function SectionCard({ title, subtitle, children }) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={1}>
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

const textFieldSx = {
  "& .MuiInputLabel-root": {
    color: "#fff",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#fff",
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    WebkitTextFillColor: "#fff",
  },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.55)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#fff",
    },
    "&.Mui-error fieldset": {
      borderColor: "#f44336",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.72)",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: "#ff8a80",
  },
};

export default function ProfileSupportPage() {
  const { t, i18n } = useTranslation();
  const isGreek = i18n.language === "gr";
  const supportReasons = t("support.reasons", { returnObjects: true });
  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const termsUrl = isGreek ? config.TERMS_URL_GR : config.TERMS_URL_EN;
  const privacyUrl = isGreek ? config.PRIVACY_URL_GR : config.PRIVACY_URL_EN;

  const [saving, setSaving] = useState(false);

  const [reason, setReason] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [body, setBody] = useState("");

  const [fieldErrors, setFieldErrors] = useState({
    reason: "",
    customTitle: "",
    body: "",
  });

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    msg: "",
  });

  const notify = (type, msg) => {
    setToast({ open: true, type, msg });
  };

  const finalTitle = reason === OTHER_REASON ? customTitle : reason;

  const clearFieldError = (field) => {
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const resetForm = () => {
    setReason("");
    setCustomTitle("");
    setBody("");
    setFieldErrors({
      reason: "",
      customTitle: "",
      body: "",
    });
  };

  const handleSubmit = async () => {
    const nextErrors = {
      reason: "",
      customTitle: "",
      body: "",
    };

    if (!reason) {
      nextErrors.reason = t("support.errorReason");
    }

    if (reason === OTHER_REASON && !customTitle.trim()) {
      nextErrors.customTitle = t("support.errorTitle");
    }

    if (!body.trim()) {
      nextErrors.body = t("support.errorBody");
    }

    if (nextErrors.reason || nextErrors.customTitle || nextErrors.body) {
      setFieldErrors(nextErrors);
      notify("error", t("support.fillRequired"));
      return;
    }

    try {
      setSaving(true);
      setFieldErrors({
        reason: "",
        customTitle: "",
        body: "",
      });

      const res = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/authentication/support-request`,
        {
          title: finalTitle,
          body,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      notify("success", res.data?.message || t("support.submitSuccess"));
      resetForm();
    } catch (error) {
      console.error("Error creating support request:", error);

      const responseData = error?.response?.data;

      if (responseData?.errors && Array.isArray(responseData.errors)) {
        const nextErrors = {
          reason: "",
          customTitle: "",
          body: "",
        };

        responseData.errors.forEach((item) => {
          const field = item.path || item.param;

          if (field === "title") {
            if (reason === OTHER_REASON) {
              nextErrors.customTitle = item.msg;
            } else {
              nextErrors.reason = item.msg;
            }
          }

          if (field === "body") {
            nextErrors.body = item.msg;
          }
        });

        setFieldErrors(nextErrors);

        notify(
          "error",
          nextErrors.reason ||
            nextErrors.customTitle ||
            nextErrors.body ||
            responseData?.error ||
            responseData?.message ||
            t("support.submitFailed")
        );
        return;
      }

      notify(
        "error",
        responseData?.error ||
          responseData?.message ||
          t("support.submitFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box py={3}>
        <SectionCard
          title={t("support.sectionTitle")}
          subtitle={t("support.subtitle")}
        >
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(fieldErrors.reason)} sx={textFieldSx}>
                  <InputLabel>{t("support.reasonLabel")}</InputLabel>
                  <Select
                    value={reason}
                    label={t("support.reasonLabel")}
                    onChange={(e) => {
                      setReason(e.target.value);
                      clearFieldError("reason");
                      clearFieldError("customTitle");
                    }}
                    sx={{
                      color: "#fff",
                      ".MuiSelect-icon": {
                        color: "#fff",
                      },
                      px: 1,
                      py: 1.8,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: "#23233a",
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.1)",
                          "& .MuiMenuItem-root.Mui-selected": {
                            bgcolor: "rgba(255,255,255,0.08)",
                          },
                          "& .MuiMenuItem-root:hover": {
                            bgcolor: "rgba(255,255,255,0.06)",
                          },
                        },
                      },
                    }}
                  >
                    {supportReasons.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {fieldErrors.reason ? (
                  <Typography sx={{ color: "#ff8a80", fontSize: "0.75rem", mt: 0.75 }}>
                    {fieldErrors.reason}
                  </Typography>
                ) : null}
              </Grid>

              {reason === OTHER_REASON ? (
                <Grid item xs={12}>
                  <TextField
                    label={t("support.titleLabel")}
                    value={customTitle}
                    onChange={(e) => {
                      setCustomTitle(e.target.value);
                      clearFieldError("customTitle");
                    }}
                    fullWidth
                    error={Boolean(fieldErrors.customTitle)}
                    helperText={fieldErrors.customTitle || t("support.titleHint")}
                    sx={textFieldSx}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <TextField
                  label={t("support.messageLabel")}
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    clearFieldError("body");
                  }}
                  fullWidth
                  multiline
                  minRows={6}
                  error={Boolean(fieldErrors.body)}
                  helperText={fieldErrors.body || t("support.messageHint")}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>

            <Alert
              severity="info"
              sx={{
                bgcolor: "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                "& .MuiAlert-icon": { color: "#fff" },
              }}
            >
              {t("support.infoNote")}
            </Alert>

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? t("support.submitting") : t("support.submit")}
              </Button>
            </Box>
          </Stack>
        </SectionCard>

        <Box mt={3}>
          <SectionCard title={t("support.contactTitle")} subtitle={t("support.contactSubtitle")}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", minWidth: 60 }}>
                  {t("support.contactPhone")}
                </Typography>
                <Link href="tel:+302104405749" underline="hover" sx={{ color: "#fff", fontWeight: 500 }}>
                  +30 210 440 5749
                </Link>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", minWidth: 60 }}>
                  {t("support.contactEmail")}
                </Typography>
                <Link href="mailto:customer-support@louder.vip" underline="hover" sx={{ color: "#fff", fontWeight: 500 }}>
                  customer-support@louder.vip
                </Link>
              </Stack>
            </Stack>
          </SectionCard>
        </Box>

        <Box mt={3}>
          <SectionCard title={t("support.legalTitle")} subtitle={t("support.legalSubtitle")}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 2,
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ArticleIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>
                      {t("support.termsTitle")}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", flexGrow: 1 }}>
                    {t("support.termsDesc")}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!termsUrl}
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.35)",
                      alignSelf: "flex-start",
                      "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    {t("support.readDoc")}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 2,
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <SecurityIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>
                      {t("support.privacyTitle")}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", flexGrow: 1 }}>
                    {t("support.privacyDesc")}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href={privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!privacyUrl}
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.35)",
                      alignSelf: "flex-start",
                      "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    {t("support.readDoc")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SectionCard>
        </Box>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            severity={toast.type}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
